import React, { useEffect,useState,useContext, useRef, useMemo } from "react"
import { Grid,Container, Typography } from '@mui/material'
import { AuthContext } from "../../auth/Auth";
import { useInView } from 'react-intersection-observer';
import { DisplayBoards } from "../boards_scheme/DisplayBoards";
import { DisplayProjectSchemes } from "../projects_scheme/DisplayProjectSchemes";
import { ProjectSchemeCreator } from "../projects_scheme/ProjectSchemeCreator";
import { DisplayCollectionSchemes } from "../collections_scheme/DisplayCollectionSchemes";
import { useBoards } from "../../global-state/useBoards";
import { useProjectSchemas, useProjectSchemaDetails } from "../../global-state/useProjectSchemas";
import { ResponsiveGridLayout } from "../components/ResponsiveGridLayout";
import { useCollections } from "../../global-state/useCollections";
import { CollectionInterface, BoardSchemeInterface, ProjectSchemeInterface, UserProfileRole  } from "ts-interfaces";

export const RecentScheme = ({ profile, permissions, designFirms, teamMembers, associatedProjectsList, setShowProjectManager, selectedProject, setSelectedProject }) => {
    const {currentUser} = useContext(AuthContext);
    const showArchived = window.location.search.indexOf('archive') > 0
    const [filterString, setFilterString] = useState('');
    const { boards, setBoards } = useBoards( (state) => ({
      boards: state.boards,
      setBoards: state.setBoards,
      getNext: state.getNext,
    }));
    useEffect( () => { 
        if(currentUser?.uid) {
            useBoards.getState().getBoards(currentUser?.uid, permissions) 
        }
    },[currentUser?.uid, permissions])

    const { projects, setProjects, isProjectsLoading } = useProjectSchemas((state) => ({
        projects: state.projects,
        setProjects: state.setProjects,
        isProjectsLoading: state.isProjectsLoading,
    }));      

    // Calculate sharedProjectIds from permissions
    const sharedProjectIds = useMemo(() => {
        if (!permissions) return [];
        return [...new Set([
            ...(permissions.projectRead || []), 
            ...(permissions.projectWrite || [])
        ])];
    }, [permissions]);

    const { ownerProjects, sharedProjects } = useMemo(() => {
        if (!projects?.length || !currentUser) return { ownerProjects: [], sharedProjects: [] };
        
        return projects
            .filter(project => showArchived || !project.archive)
            .filter(project => !filterString || project.name?.toLowerCase().includes(filterString.toLowerCase()))
            .reduce<{ ownerProjects: ProjectSchemeInterface[], sharedProjects: ProjectSchemeInterface[] }>((result, project) => {
                if (project.owner === currentUser.uid) {
                    result.ownerProjects.push(project);
                } else if (sharedProjectIds.includes(project.id)) {
                    result.sharedProjects.push(project);
                }
                return result;
            }, { ownerProjects: [], sharedProjects: [] });
    }, [projects, currentUser, showArchived, filterString, sharedProjectIds]);

    const recentProjects = useMemo(() => { 
        return [...ownerProjects, ...sharedProjects].filter(project => !project.archive).slice(0, 4);
    }, [ownerProjects, sharedProjects]);

    // Check if user is free tier (not PRO)
    const isFreeUser = profile?.userRole !== UserProfileRole.PRO;

    const recentBoards = useMemo(() => {
        const tmpBoards = boards.filter(board => !board.archive);
        // For free tier users, show only boards owned by the user
        const filteredBoards = isFreeUser 
            ? tmpBoards.filter(board => board.owner === currentUser?.uid || board.user === currentUser?.uid)
            : tmpBoards;
        return filteredBoards.slice(0, 4);
    },[boards, isFreeUser, currentUser?.uid])

    const rootProjectContainer = useRef<HTMLDivElement>(null)
    const { ref, inView } = useInView({root:rootProjectContainer.current,threshold: 0,});
    const [projectThumbnails, setProjectThumbnails] = useState<Record<string, string[]>>({})
    const [showCreatorModal, setShowCreatorModal] = useState(false)
    const { collections, currentUserId, isLoadingUserCollections, hasMoreUserCollections, getNext } = useCollections( (state) => ({
        collections: state.collections, // ✅ Subscribe to underlying state
        currentUserId: state.currentUserId,
        isLoadingUserCollections: state.isLoadingUserCollections,  // ✅ Updated to new consistent naming
        hasMoreUserCollections: state.hasMoreUserCollections,  // ✅ Updated to new consistent naming
        getNext: state.getNext
    }));
    const [initialLoadAttempted, setInitialLoadAttempted] = useState(false);

    useEffect(() => { 
        if (currentUser?.uid) {
            useCollections.getState().fetchCollections(currentUser.uid, permissions)
                .then(() => setInitialLoadAttempted(true));
        }
    },[currentUser?.uid]);

    const loadItems = async () => {
        if (isLoadingUserCollections === false && hasMoreUserCollections) {  // ✅ Updated to use new property name
            await getNext();
        } 
    }

    useEffect(() => {  if (inView==true && initialLoadAttempted){ loadItems() } },[inView, initialLoadAttempted])

    // Filter collections for free tier users to show only owned collections
    const recentCollections = useMemo(() => {
        const tmpCollections = collections?.slice(0, 4) || [];
        return isFreeUser 
            ? tmpCollections.filter(collection => 
                collection.uid === currentUser?.uid || collection.owner === currentUser?.uid
            )
            : tmpCollections;
    }, [collections, isFreeUser, currentUser?.uid])

    const updateProject = (project: ProjectSchemeInterface) => {
        // add project to data if it doesn't exist, or update it if it does
        const existingProjectIndex = recentProjects.findIndex(p => p.id === project.id);
        if (existingProjectIndex !== -1) {
            const updatedData = [...recentProjects];
            updatedData[existingProjectIndex] = project;
            setProjects(updatedData);
            setTimeout(() => {
                useProjectSchemaDetails.getState().syncUserProjects();
            }, 0);
        }
    }

    const handleBoardUpdate = (board: BoardSchemeInterface, addBoard = false) =>  { 
        if (addBoard) { 
            setBoards([board, ...boards]); 
        } 
        else {
            const updatedBoards = boards.map(b => b.id === board.id ? board : b);
            setBoards(updatedBoards);
        }
    }

    const handleCollectionUpdate = (collection: CollectionInterface, addCollection = false, isDeleted = false) =>  { 
        if (addCollection) { 
            useCollections.getState().saveCollection(collection);
        } 
        else if (isDeleted) {
            useCollections.getState().removeCollection(collection.id);
        } else {
            useCollections.getState().updateCollection(collection);
        }
    }

    return (
    <Grid ref={rootProjectContainer}>
        {showCreatorModal && <ProjectSchemeCreator project={selectedProject} setShowCreatorModal={setShowCreatorModal} updateProject={updateProject} />}
        <Container maxWidth={false}>
            <Grid sx={{ marginTop: '20px' }} />
            {/* Projects section - only show for PRO users */}
            {!isFreeUser && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                        Recent Projects
                    </Typography>
                    <ResponsiveGridLayout>
                        <DisplayProjectSchemes 
                            data={recentProjects} 
                            setData={setProjects} 
                            designFirms={designFirms} 
                            teamMembers={teamMembers} 
                            selectedProject={selectedProject} 
                            setSelectedProject={setSelectedProject} 
                            setShowProjectManager={setShowProjectManager} 
                            projectThumbnails={projectThumbnails} 
                            setShowCreatorModal={setShowCreatorModal} 
                            onProjectUpdate={updateProject}
                        />
                    </ResponsiveGridLayout>
                    
                    <Grid sx={{ marginTop: '20px', marginBottom: '20px', borderTop: '1px solid #CCC' }} />
                </>
            )}
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                Recent Boards
            </Typography>
            <ResponsiveGridLayout>
                <DisplayBoards 
                    data={recentBoards} 
                    onBoardUpdate={handleBoardUpdate} 
                    designFirms={designFirms} 
                    teamMembers={teamMembers} 
                />
            </ResponsiveGridLayout>

            <Grid sx={{ marginTop: '20px', marginBottom: '20px', borderTop: '1px solid #CCC' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '20px' }}>
                Recent Collections
            </Typography>
            <ResponsiveGridLayout>
                <DisplayCollectionSchemes 
                    data={recentCollections} 
                    onCollectionUpdate={handleCollectionUpdate} 
                    designFirms={designFirms} 
                    teamMembers={teamMembers} 
                    associatedProjectsList={associatedProjectsList} 
                />
            </ResponsiveGridLayout>

        </Container>
        <Grid key={'placeholder'} ref={ref} item xs={6}>
            <img draggable={false} src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg==" height={1} alt="Red dot" />
        </Grid>
    </Grid>
    )
}